import { Calendar, CalendarCheck, Document, Earth, Flight, ShieldCheck } from '@insify/design-system/line-icons';
import { Trans, useTranslation } from 'react-i18next';

import { useContent } from './useContent';
import styles from './useExclusionContent.module.css';
import { useConditionsQuery } from './useQueries';

import type { ExclusionCoverageSegment, ExclusionType } from '../models/exclusions';
import type { ExclusionsOverviewProps } from '@insify/funnel-components';

// It matches the order of appearance on the Shop page
// For now we only have one exclusion
const exclusionsOrder: ExclusionCoverageSegment[] = ['professionalLiability'];

export const useExclusionsContent = (): ExclusionsOverviewProps<ExclusionType, ExclusionCoverageSegment> => {
  const { exclusions: richContentExclusions } = useContent() || {};
  const { t } = useTranslation(['common', 'funnel']);
  const { data: conditions } = useConditionsQuery();

  const { data: { exclusions: exclusionsSegmentSpecific = [] } = {} } = useConditionsQuery();
  const staticExclusion = conditions?.declarations?.map((declaration) => ({
    type: declaration,
  }));

  return {
    exclusions: staticExclusion,
    exclusionsSegmentSpecific,
    exclusionsSegmentSpecificOrder: exclusionsOrder,
    icons: {
      activeClaimAwareness: <Calendar />,
      professionalLiability: <Document />,
      hiscoxFrenchCompany: <Earth />,
      hiscoxNoDoubleInsurance: <Calendar />,
      hiscoxNoPastOrPotentialClaims: <CalendarCheck />,
      hiscoxSecuritySpecific: <ShieldCheck />,
      hiscoxTourismSpecific: <Flight />,
    },
    translations: {
      back: t('common:back'),
      submit: t('common:continue'),
      accordionActiveClaimAwarenessDescription: t('funnel:exclusions_active_claim_awareness'),
      accordionHiscoxFrenchCompanyDescription: (
        <Trans i18nKey='funnel:exclusions_hiscox_french_compnay' components={{ p: <P /> }} />
      ),
      accordionHiscoxNoDoubleInsuranceDescription: (
        <Trans i18nKey='funnel:exclusions_hiscox_no_double_insurance' components={{ p: <P /> }} />
      ),
      accordionHiscoxNoPastOrPotentialClaimsDescription: (
        <Trans i18nKey='funnel:exclusions_hiscox_no_past_or_potential_claims' components={{ p: <P /> }} />
      ),
      accordionHiscoxSecuritySpecificDescription: (
        <Trans i18nKey='funnel:exclusions_hiscox_security_specific' components={{ p: <P />, ul: <UL />, li: <LI /> }} />
      ),
      accordionHiscoxTourismSpecificDescription: (
        <Trans i18nKey='funnel:exclusions_hiscox_tourism_specific' components={{ p: <P /> }} />
      ),
      accordionProfessionalLiabilityHeader: t('funnel:exclusions_professional_liabilty_title'),
      accordionProfessionalLiabilityToggle: t('funnel:exclusions_professional_liabilty_toggle'),
      accordionProfessionalLiabilityDetailsHeader: t('funnel:exclusions_professional_liabilty_inner_header'),
    },
    content: richContentExclusions,
  };
};

const P = ({ children }: { children?: React.ReactNode }) => <p className={styles.exclusionParagraph}>{children}</p>;

const UL = ({ children }: { children?: React.ReactNode }) => <ul className={styles.exclusionList}>{children}</ul>;

const LI = ({ children }: { children?: React.ReactNode }) => <li className={styles.exclusionListItem}>{children}</li>;
